import algoliasearch from 'algoliasearch/lite';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ArtistSearchResult, SongSearchResult } from '../data/store/useSearch';

/**
 * Search is read-only from the browser (security finding S03).
 *
 * - Indexing happens only in Cloud Functions (syncSongSearch /
 *   syncArtistSearch triggers, reindexSearch callable). Nothing here can
 *   write, delete or change index settings, and no Algolia key is compiled
 *   into the bundle.
 * - Public material: searched directly with a short-lived secured key from
 *   the getSearchKey callable. That key can only query this instance's two
 *   public indexes and only matches `visibility:public` records.
 * - Non-public material (class songs, members-only profiles): the
 *   searchRestricted callable runs the query server-side with the caller's
 *   real access filter; signed-out visitors skip it.
 */

interface SearchKeyResponse {
  appId: string;
  key: string;
  validUntil: number; // unix seconds
  indices: { songs: string; artists: string };
}

interface RestrictedResponse {
  songs: SongSearchResult[];
  artists: ArtistSearchResult[];
  nbPages: number;
}

interface ArtistSearchProps {
  nbPages: number;
  artists: ArtistSearchResult[];
}

interface SongSearchProps {
  nbPages: number;
  songs: SongSearchResult[];
}

interface MultiSearchProps {
  nbPages: number;
  artists: ArtistSearchResult[];
  songs: SongSearchResult[];
}

type Target = 'songs' | 'artists' | 'multi';

/** Refresh the key this long before it expires. */
const KEY_REFRESH_MARGIN_S = 5 * 60;

const mergeById = <T extends { id: string }>(a: T[], b: T[]): T[] => {
  const seen = new Set(a.map((x) => x.id));
  return [...a, ...b.filter((x) => !seen.has(x.id))];
};

export class SearchService {
  private static instance: SearchService;
  private keyPromise: Promise<{ client: ReturnType<typeof algoliasearch>; meta: SearchKeyResponse }> | null = null;
  private keyExpiresAt = 0;

  public static please(): SearchService {
    if (!SearchService.instance)
      SearchService.instance = new SearchService();
    return SearchService.instance;
  }

  private publicClient() {
    const now = Date.now() / 1000;
    if (!this.keyPromise || now >= this.keyExpiresAt) {
      const getSearchKey = httpsCallable<void, SearchKeyResponse>(getFunctions(), 'getSearchKey');
      this.keyPromise = getSearchKey().then(({ data }) => {
        this.keyExpiresAt = data.validUntil - KEY_REFRESH_MARGIN_S;
        return { client: algoliasearch(data.appId, data.key), meta: data };
      });
      // Don't cache a failure.
      this.keyPromise.catch(() => {
        this.keyPromise = null;
      });
    }
    return this.keyPromise;
  }

  private async restricted(query: string, page: number, target: Target): Promise<RestrictedResponse> {
    if (!firebase.auth().currentUser) return { songs: [], artists: [], nbPages: 0 };
    try {
      const searchRestricted = httpsCallable<
        { query: string; page: number; target: Target },
        RestrictedResponse
      >(getFunctions(), 'searchRestricted');
      const { data } = await searchRestricted({ query, page, target });
      return data;
    } catch (err) {
      // Public results still render if the restricted half fails.
      console.error('[SearchService] searchRestricted error', err);
      return { songs: [], artists: [], nbPages: 0 };
    }
  }

  async searchArtists(query: string, page: number): Promise<ArtistSearchProps> {
    const [{ client, meta }, priv] = await Promise.all([
      this.publicClient(),
      this.restricted(query, page, 'artists'),
    ]);
    const { hits, nbPages } = await client.initIndex(meta.indices.artists).search(query, { page });
    return {
      nbPages: Math.max(nbPages, priv.nbPages),
      artists: mergeById(hits.map((x) => x as unknown as ArtistSearchResult), priv.artists),
    };
  }

  async searchSongs(query: string, page: number): Promise<SongSearchProps> {
    const [{ client, meta }, priv] = await Promise.all([
      this.publicClient(),
      this.restricted(query, page, 'songs'),
    ]);
    const { hits, nbPages } = await client.initIndex(meta.indices.songs).search(query, { page });
    return {
      nbPages: Math.max(nbPages, priv.nbPages),
      songs: mergeById(hits.map((x) => x as unknown as SongSearchResult), priv.songs),
    };
  }

  async searchMulti(query: string, page: number): Promise<MultiSearchProps> {
    const [{ client, meta }, priv] = await Promise.all([
      this.publicClient(),
      this.restricted(query, page, 'multi'),
    ]);
    const { results } = await client.search([
      { indexName: meta.indices.artists, query, params: { hitsPerPage: 1000 } },
      { indexName: meta.indices.songs, query, params: { page } },
    ]);
    const artistsResult: any = results.find((r: any) => r.index === meta.indices.artists);
    const songsResult: any = results.find((r: any) => r.index === meta.indices.songs);
    const artists = artistsResult ? artistsResult.hits.map((x: any) => x as ArtistSearchResult) : [];
    const songs = songsResult ? songsResult.hits.map((x: any) => x as SongSearchResult) : [];
    return {
      nbPages: Math.max(songsResult ? songsResult.nbPages : 0, priv.nbPages),
      artists: mergeById(artists, priv.artists),
      songs: mergeById(songs, priv.songs),
    };
  }
}
